import { sandboxFileSchema } from "schemas/sandboxFiles";
import { z } from "zod";

/**
 * Agent Files schemas — the unified, Postgres-backed Drive (Postgres owns the
 * file graph, R2 owns the bytes). Distinct from `schemas/file`.
 */

export const agentFileKindSchema = z
  .enum(["file", "folder"])
  .describe("AgentFileKind");

/**
 * - `files`  → user-facing Drive content (shareable).
 * - `system` → agent state and branding (not user-visible).
 * - `chat`   → chat-composer uploads: real fileId rows hidden from Drive views.
 */
export const agentFileNamespaceSchema = z
  .enum(["files", "system", "chat"])
  .describe("AgentFileNamespace");

/**
 * - `private` → reachable only by the owner and explicit grantees.
 * - `project` → every project member is an implicit editor (the team Drive).
 */
export const agentFileVisibilitySchema = z
  .enum(["private", "project"])
  .describe("AgentFileVisibility");

/** A grantable role. `owner` is implicit (not stored as a grant). */
export const agentFileRoleSchema = z
  .enum(["viewer", "commenter", "editor"])
  .describe("AgentFileRole");

/**
 * A grant's grantee:
 * - `user`    → one person (`userId` set).
 * - `project` → every project member (`userId` null); replaces the retired
 *   `visibility = "project"` flag as the "share with the whole team" mechanism.
 * - `unauthenticated` → anyone with the link (`userId` null).
 */
export const agentFileGrantAudienceSchema = z
  .enum(["user", "project", "unauthenticated"])
  .describe("AgentFileGrantAudience");

/** The caller's effective role on a file, resolved across ownership + grants. */
export const agentFileAccessRoleSchema = z
  .enum(["owner", "editor", "commenter", "viewer"])
  .describe("AgentFileAccessRole");

/** Minimal member identity for share lists and version authorship. */
export const agentFileMemberSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    avatarUrl: z.string().nullable(),
  })
  .describe("AgentFileMember");

/**
 * A node returned to clients: DB columns plus derived caller-scoped fields
 * (cached `sizeBytes`/`contentType` and the caller's `effectiveRole`).
 */
export const agentFileSchema = z
  .object({
    id: z.string(),
    // Null for a user-scope file; every project-scoped file carries its project id.
    projectId: z.string().nullable(),
    parentId: z.string().nullable(),
    ownerId: z.string().nullable(),
    name: z.string(),
    kind: agentFileKindSchema,
    namespace: agentFileNamespaceSchema,
    visibility: agentFileVisibilitySchema,
    sizeBytes: z.number().nullable(),
    contentType: z.string().nullable(),
    tags: z
      .array(z.string())
      .describe(
        "Brand context tags the file carries (`schemas/projectContext` vocabulary); empty for most files.",
      ),
    url: z
      .string()
      .nullable()
      .describe(
        "Short-lived signed GET URL for the file's bytes (files only).",
      ),
    effectiveRole: agentFileAccessRoleSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .describe("AgentFile");

export const agentFileGrantSchema = z
  .object({
    id: z.string(),
    projectId: z.string(),
    fileId: z.string(),
    audience: agentFileGrantAudienceSchema,
    // Null for project and unauthenticated grants.
    userId: z.string().nullable(),
    role: agentFileRoleSchema,
    createdById: z.string(),
    revokedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    // Null for non-user grants, or when the granted user is missing.
    user: agentFileMemberSchema.nullable(),
  })
  .describe("AgentFileGrant");

/* -------------------------------------------------------------------------- */
/* Listing                                                                    */
/* -------------------------------------------------------------------------- */

/** A pre-defined Drive view, used by the app's tabs / FUSE root mounts. */
export const agentFileRootViewSchema = z
  .enum(["mine", "shared-with-me", "team"])
  .describe("AgentFileRootView");

export const agentFileListResponseSchema = z
  .object({
    files: z.array(agentFileSchema),
    /**
     * Breadcrumb ancestry from root → the listed folder, inclusive of the
     * folder itself. Empty for a root view (which has no enclosing folder).
     */
    breadcrumb: z.array(agentFileSchema),
  })
  .describe("AgentFileListResponse");

export const agentFileAccessibleSortSchema = z
  .enum(["createdAtDesc", "createdAtAsc", "name"])
  .describe("AgentFileAccessibleSort");

export const agentFilePageResponseSchema = z
  .object({
    files: z.array(agentFileSchema),
    nextOffset: z
      .number()
      .int()
      .nullable()
      .describe("Offset of the next page; null when exhausted."),
  })
  .describe("AgentFilePageResponse");

export const agentFileResponseSchema = z
  .object({ file: agentFileSchema })
  .describe("AgentFileResponse");

export const agentFileSandboxPathResponseSchema = z
  .object({ path: z.string().nullable() })
  .describe("AgentFileSandboxPathResponse");

/* -------------------------------------------------------------------------- */
/* Mutations                                                                  */
/* -------------------------------------------------------------------------- */

export const agentFileCreateRequestSchema = z
  .object({
    // Caller-assigned id so optimistic UI keeps one identity end to end. Omit to
    // let the server mint one; a collision throws (standard create semantics).
    id: z.uuid().optional(),
    parentId: z.uuid().nullable(),
    name: z.string().min(1).max(255),
    kind: agentFileKindSchema,
    namespace: agentFileNamespaceSchema.optional(),
    // The mount's Drive view: `"mine"` + `"system"` scopes the row to the user-global (projectless) context; omitted → project-scoped.
    view: agentFileRootViewSchema.optional(),
    visibility: agentFileVisibilitySchema.optional(),
    // Supply for a `file` whose bytes PUT directly to R2: the response carries a
    // presigned `uploadUrl` and the row's size/type are stamped up front.
    contentType: z.string().min(1).max(255).optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
    // Collision policy: `"error"` (default) keeps exact-path semantics for
    // agents/FUSE; `"rename"` auto-suffixes `name (n).ext` for human uploads.
    onConflict: z.enum(["error", "rename"]).optional(),
  })
  .describe("AgentFileCreateRequest");

export const agentFileCreateResponseSchema = z
  .object({
    file: agentFileSchema,
    uploadUrl: z
      .string()
      .nullable()
      .describe(
        "Presigned PUT URL for the new file's bytes; null for folders and for file rows created without content metadata.",
      ),
  })
  .describe("AgentFileCreateResponse");

export const agentFileUpdateRequestSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    parentId: z.uuid().nullish(),
    visibility: agentFileVisibilitySchema.optional(),
  })
  .describe("AgentFileUpdateRequest");

export const agentFileMutationResponseSchema = z
  .object({ ok: z.literal(true) })
  .describe("AgentFileMutationResponse");

/* -------------------------------------------------------------------------- */
/* Content                                                                    */
/* -------------------------------------------------------------------------- */

/** Direct (base64) upload — web convenience for small drag-and-drop files. */
export const agentFileUploadRequestSchema = z
  .object({
    parentId: z.uuid().nullable(),
    name: z.string().min(1).max(255),
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().nonnegative(),
    dataBase64: z
      .string()
      .min(1)
      .describe("Base64-encoded bytes; a `data:` URL prefix is allowed."),
    // Only consulted at a view root (a folder inherits its parent's visibility),
    // so a Team-tab root upload lands in the team Drive instead of private files.
    visibility: agentFileVisibilitySchema.optional(),
  })
  .describe("AgentFileUploadRequest");

export const agentFileStrongEtagSchema = z
  .string()
  .max(256)
  .regex(/^"[\x21\x23-\x2b\x2d-\x7e]+"$/)
  .describe("AgentFileStrongEtag");

export const agentFileContentConditionSchema = z
  .discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("match"),
      etag: agentFileStrongEtagSchema,
    }),
    z.strictObject({ kind: z.literal("missing") }),
  ])
  .describe("AgentFileContentCondition");

export const agentFileContentGuardSchema = z
  .strictObject({
    expectedMetadataVersion: z.string().min(1).max(128),
    contentEtag: agentFileStrongEtagSchema,
    renameTo: z.string().min(1).max(255).optional(),
  })
  .describe("AgentFileContentGuard");

export const agentFileContentResponseSchema = z
  .object({
    downloadUrl: z
      .string()
      .describe("Short-lived presigned GET URL for the file's bytes."),
  })
  .extend({ contentExists: z.boolean().optional() })
  .describe("AgentFileContentResponse");

export const agentFileWriteUrlResponseSchema = z
  .object({
    uploadUrl: z
      .string()
      .describe("Presigned PUT URL for the file's stable R2 key."),
  })
  .describe("AgentFileWriteUrlResponse");

export const agentFileWriteUrlRequestSchema = z
  .object({
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().nonnegative(),
  })
  .extend({
    contentCondition: agentFileContentConditionSchema.optional(),
    renameTo: z
      .string()
      .min(1)
      .max(255)
      .optional()
      .describe(
        "Pre-validates the rename (name policy and collision) so the upload fails before any bytes move; it never renames. The rename itself is reportContent's guard.renameTo.",
      ),
  })
  .describe("AgentFileWriteUrlRequest");

export const agentFileReportContentRequestSchema =
  agentFileWriteUrlRequestSchema
    .omit({ contentCondition: true, renameTo: true })
    .extend({ guard: agentFileContentGuardSchema.optional() })
    .describe("AgentFileReportContentRequest");

/* -------------------------------------------------------------------------- */
/* Sharing                                                                    */
/* -------------------------------------------------------------------------- */

export const agentFileShareListResponseSchema = z
  .object({
    owner: agentFileMemberSchema.nullable(),
    grants: z.array(agentFileGrantSchema),
  })
  .describe("AgentFileShareListResponse");

export const agentFileGrantUpsertRequestSchema = z
  .object({
    // Defaults to `user` so pre-audience callers (a bare `{ userId, role }`)
    // keep working unchanged. `userId` is required for a `user` grant and
    // ignored for a `project` or `unauthenticated` grant (validated server-side).
    audience: agentFileGrantAudienceSchema.default("user"),
    userId: z.uuid().nullish(),
    role: agentFileRoleSchema.nullable(),
  })
  .describe("AgentFileGrantUpsertRequest");

export const agentFileGrantResponseSchema = z
  .object({ grant: agentFileGrantSchema.nullable() })
  .describe("AgentFileGrantResponse");

export const agentFileGrantListResponseSchema = z
  .object({ grants: z.array(agentFileGrantSchema) })
  .describe("AgentFileGrantListResponse");

/* -------------------------------------------------------------------------- */
/* Drive verbs (ref-addressed: path or id)                                    */
/* -------------------------------------------------------------------------- */

/**
 * Address a Drive file either by absolute `/workspace/...` path or by its stable
 * id. Every verb on `client.drive.fs` takes a ref; the caller chooses the
 * addressing mode (path-keyed apps vs the id-keyed deep-link apps like draw)
 * without the route ever carrying a `by-path`/`by-id` namespace.
 */
export const agentFileRefSchema = z
  .discriminatedUnion("kind", [
    z.object({ kind: z.literal("path"), path: z.string().min(1) }),
    z.object({ kind: z.literal("id"), id: z.uuid() }),
  ])
  .describe("AgentFileRef");

/**
 * Batch read by path. A path that resolves to no file (or one the caller can't
 * read) is `null`; an existing file with no bytes is `""`. Mirrors the sandbox
 * `readFiles` RPC, keeping the inline read interchangeable with `SandboxFs`.
 */
export const agentFileReadRequestSchema = z
  .object({
    projectId: z.uuid(),
    paths: z.array(z.string().min(1)).min(1),
    // `base64` returns raw R2 bytes base64-encoded; omitted/`utf8` returns text.
    encoding: z.enum(["utf8", "base64"]).optional(),
  })
  .describe("AgentFileReadRequest");

export const agentFileReadResponseSchema = z
  .object({ files: z.record(z.string(), z.string().nullable()) })
  .describe("AgentFileReadResponse");

/** Inline write by path (content in the body). Returns the persisted size. */
export const agentFileWriteRequestSchema = z
  .object({
    projectId: z.uuid(),
    path: z.string().min(1),
    content: z.string(),
    contentType: z.string().min(1).max(255).optional(),
    // `base64` decodes `content` to raw bytes before storing; omitted/`utf8` stores text.
    encoding: z.enum(["utf8", "base64"]).optional(),
  })
  .describe("AgentFileWriteRequest");

export const agentFileWriteResponseSchema = z
  .object({ sizeBytes: z.number().int().nonnegative() })
  .describe("AgentFileWriteResponse");

export const agentFileFindRequestSchema = z
  .object({
    projectId: z.uuid(),
    directory: z.string().min(1),
    patterns: z.array(z.string().min(1)).min(1),
    ignore: z.array(z.string().min(1)).optional(),
  })
  .describe("AgentFileFindRequest");

export const agentFileFindResponseSchema = z
  .object({ files: z.array(sandboxFileSchema) })
  .describe("AgentFileFindResponse");

/** Delete a file by ref (path or id); a missing path is a no-op. */
export const agentFileDeleteRequestSchema = z
  .object({
    projectId: z.uuid(),
    ref: agentFileRefSchema,
  })
  .describe("AgentFileDeleteRequest");

/** Stat a file by ref (path or id) -> the full node. */
export const agentFileStatRequestSchema = z
  .object({
    projectId: z.uuid(),
    ref: agentFileRefSchema,
  })
  .describe("AgentFileStatRequest");

/** Presign a direct-R2 download for a file's bytes, addressed by ref. */
export const agentFilePresignedReadRequestSchema = z
  .object({
    projectId: z.uuid(),
    ref: agentFileRefSchema,
  })
  .describe("AgentFilePresignedReadRequest");

export const agentFilePresignedReadResponseSchema = z
  .object({
    downloadUrl: z
      .string()
      .describe("Short-lived presigned GET URL for the file's bytes."),
    sizeBytes: z.number().int().nonnegative().nullable(),
  })
  .describe("AgentFilePresignedReadResponse");

/** Presign a direct-R2 PUT at the file's stable key, addressed by ref. */
export const agentFilePresignedWriteRequestSchema = z
  .object({
    projectId: z.uuid(),
    ref: agentFileRefSchema,
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().nonnegative(),
  })
  .describe("AgentFilePresignedWriteRequest");

/**
 * Record bytes written out-of-band (the direct R2 PUT) so the row's cached
 * `sizeBytes`/`contentType` stay in sync. Addressed by ref; returns the node.
 */
export const agentFileConfirmWriteRequestSchema = z
  .object({
    projectId: z.uuid(),
    ref: agentFileRefSchema,
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().nonnegative(),
  })
  .describe("AgentFileConfirmWriteRequest");

/**
 * Ordered writes/deletes applied as one serialized unit per project (advisory
 * lock; no rollback), so multi-file mutations can't interleave.
 */
export const agentFileBatchOperationSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("write"),
      path: z.string().min(1),
      content: z.string(),
    }),
    z.object({ kind: z.literal("delete"), path: z.string().min(1) }),
  ])
  .describe("AgentFileBatchOperation");

export const agentFileBatchRequestSchema = z
  .object({
    projectId: z.uuid(),
    operations: z.array(agentFileBatchOperationSchema).min(1).max(50),
  })
  .describe("AgentFileBatchRequest");
